import { useEffect, useCallback, useState, useRef } from 'react';
import type { MessageAttachment } from '@goodchat/shared/types';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.15;

interface MediaViewerProps {
  attachment: MessageAttachment;
  authorName?: string;
  timestamp?: string;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function MediaViewer({
  attachment,
  authorName,
  timestamp,
  onClose,
  onPrev,
  onNext,
}: MediaViewerProps) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset zoom and pan when image changes
  const attachmentId = attachment.id ?? attachment.fileUrl;
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [attachmentId]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + 0.5, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => {
      const next = Math.max(z - 0.5, MIN_ZOOM);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrev?.();
          break;
        case 'ArrowRight':
          onNext?.();
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
      }
    },
    [onClose, onPrev, onNext, zoomIn, zoomOut, resetZoom],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Scroll-wheel zoom handler
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom((z) => {
        const next = Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
        if (next <= 1) {
          setPan({ x: 0, y: 0 });
        }
        return next;
      });
    },
    [],
  );

  // Also need a native event listener to preventDefault on the container
  // because React's onWheel is passive by default in React 17+
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Double-click to reset zoom
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (zoom > 1) {
        resetZoom();
      } else {
        setZoom(2);
      }
    },
    [zoom, resetZoom],
  );

  // Pan handlers -- only active when zoomed in
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      e.preventDefault();
      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      panStartRef.current = { x: pan.x, y: pan.y };
    },
    [zoom, pan],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || zoom <= 1) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setPan({
        x: panStartRef.current.x + dx,
        y: panStartRef.current.y + dy,
      });
    },
    [isDragging, zoom],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Also listen for mouseup on window to catch drags that end outside the container
  useEffect(() => {
    if (isDragging) {
      const handler = () => setIsDragging(false);
      window.addEventListener('mouseup', handler);
      return () => window.removeEventListener('mouseup', handler);
    }
  }, [isDragging]);

  const isImage = attachment.fileType.startsWith('image/');
  const isVideo = attachment.fileType.startsWith('video/');
  const isZoomed = zoom > 1;
  const zoomPercent = Math.round(zoom * 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      {/* Backdrop click to close */}
      <div className="absolute inset-0" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} />

      {/* Top bar */}
      <div className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent px-4 py-3">
        <div className="text-sm text-white/80">
          {authorName && <span className="font-medium">{authorName}</span>}
          {timestamp && (
            <span className="ml-2 text-white/60">
              {new Date(timestamp).toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Download button */}
          <a
            href={attachment.fileUrl}
            download={attachment.fileName ?? 'download'}
            className="rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            title="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
          {/* Close button */}
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Zoom controls (bottom-right corner) */}
      {isImage && (
        <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-lg bg-black/60 px-2 py-1.5 backdrop-blur-sm">
          <button
            onClick={(e) => {
              e.stopPropagation();
              zoomOut();
            }}
            disabled={zoom <= MIN_ZOOM}
            className="rounded p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            title="Zoom out (-)"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              resetZoom();
            }}
            className="min-w-[3rem] px-1 text-center text-xs font-medium text-white/80 transition-colors hover:text-white"
            title="Reset zoom (0)"
          >
            {zoomPercent}%
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              zoomIn();
            }}
            disabled={zoom >= MAX_ZOOM}
            className="rounded p-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            title="Zoom in (+)"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      )}

      {/* Navigation arrows */}
      {onPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 z-10 rounded-full bg-black/40 p-3 text-white/80 transition-colors hover:bg-black/60 hover:text-white"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      {onNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-4 z-10 rounded-full bg-black/40 p-3 text-white/80 transition-colors hover:bg-black/60 hover:text-white"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Media content */}
      <div
        ref={containerRef}
        className="relative z-[1] max-h-[90vh] max-w-[90vw] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onDoubleClick={isImage ? handleDoubleClick : undefined}
        onMouseDown={isImage ? handleMouseDown : undefined}
        onMouseMove={isImage ? handleMouseMove : undefined}
        onMouseUp={isImage ? handleMouseUp : undefined}
        style={{
          cursor: isImage
            ? isDragging
              ? 'grabbing'
              : isZoomed
                ? 'grab'
                : 'zoom-in'
            : 'default',
        }}
      >
        {isImage && (
          <img
            src={attachment.fileUrl}
            alt={attachment.fileName ?? ''}
            className="max-h-[90vh] max-w-[90vw] object-contain select-none"
            draggable={false}
            style={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: 'center center',
              transition: isDragging ? 'none' : 'transform 0.15s ease-out',
            }}
          />
        )}
        {isVideo && (
          <video
            src={attachment.fileUrl}
            controls
            autoPlay
            className="max-h-[90vh] max-w-[90vw]"
          />
        )}
      </div>
    </div>
  );
}
